<template>
  <div class="layout-center-wrap">
    <!-- 상단 타이틀 -->
    <section class="top_head">
      <div class="page-tit__wrap">
        <h1 class="page-tit__sub">취약점 제보</h1>
        <p class="page-tit">취약점 신고 접수</p>
      </div>
      <div class="write-auto-save" aria-label="자동 저장 안내 영역">
        <p
          v-if="defaultAutoSaveInterval !== null"
          class="write-auto-save__status"
          aria-live="polite"
          aria-atomic="true"
          aria-describedby="write-auto-save-interval"
        >
          <span class="write-auto-save__time">
            {{ formattedRemainingTime }}
          </span>
          후 자동 저장됩니다.
        </p>
        <div class="write-auto-save__wrap end">
          <label for="write-auto-save-interval" class="sr-only"
            >자동 저장 주기 선택</label
          >
          <select
            name=""
            id="write-auto-save-interval"
            class="write-auto-save__live"
            v-model="defaultAutoSaveInterval"
          >
            <option :value="null">안함</option>
            <option value="1">1분마다 저장</option>
            <option value="2">2분마다 저장</option>
            <option value="5">5분마다 저장</option>
            <option value="10">10분마다 저장</option>
          </select>
          <div
            class="write-auto-save__actions"
            role="group"
            aria-label="저장 옵션"
          >
            <button
              type="button"
              class="btn btn--medium_blue btn--m"
              @click="loadTempData"
            >
              이전 내용 불러오기
            </button>
            <button
              type="button"
              class="btn btn--blue btn--m"
              aria-busy="false"
              @click="tempSave"
            >
              임시저장
            </button>
          </div>
        </div>
      </div>
    </section>
    <!-- 탭 메뉴 -->
    <Tabs
      :tabs="
        memberType === 'member'
          ? ['확인사항', '취약점 정보']
          : ['개인정보작성', '확인사항', '취약점 정보']
      "
      v-model="selectedTab"
      role="tablist"
    />

    <!-- 탭 내용 -->

    <component
      v-for="(tab, index) in tabComponents"
      :key="componentKey + index"
      :is="tab"
      v-show="selectedTab === index"
      ref="childRef"
      v-model="totalInfo"
      serviceType="report"
      :memberType="memberType"
      :rewardNmCodes="rewardNmCodes"
      :vulAttackTypeCodes="vulAttackTypeCodes"
      :vulEffectCodes="vulEffectCodes"
      :makrSeCdCodes="makrSeCdCodes"
      :edit="true"
      @changeTab="changeTab"
    />
    <div class="btn__wrap type-bottom-board">
      <button
        v-if="isVulInfoTab"
        type="button"
        class="btn btn--l btn--darker_blue"
        @click="submitReport"
      >
        접수
      </button>
    </div>
  </div>
</template>

<script>
import gt from "@/component/GetTokenValue.js";
import Tabs from "@/component/Tabs.vue";
import VulEval from "@/views/eval/CvssEval.vue";
import reportHandler from "@/views/report/ReportHandler";
import CheckList from "@/views/report/submission/CommonUserTab/CheckListTab.vue";
import PersonalInfo from "@/views/report/submission/CommonUserTab/PersonalInfoTab.vue";
import VulInfo from "@/views/report/submission/CommonUserTab/VulInfoTab.vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { deleteAllIndexedDBs } from "@/views/tempSave/TempSave.js";
import {
  computed,
  onMounted,
  inject,
  ref,
  watch,
  nextTick,
  onUnmounted,
} from "vue";

let countdownTimer = null; // 카운트다운 인터벌

export default {
  components: { PersonalInfo, CheckList, VulInfo, VulEval, Tabs },
  setup() {
    const lodash = inject("lodash");
    const axios = inject("axios");
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const showLoading = () => store.dispatch("loading/show");
    const hideLoading = () => store.dispatch("loading/hide");

    // data 정의
    const componentKey = ref(0);
    // 화면 진입 정보(회원/비회원/해외) 읽어 type 지정해주는 코드 추가 필요
    const memberType = ref("member");
    const selectedTab = ref(0); // 탭 인덱스 관리
    const defaultAutoSaveInterval = ref("2"); // 기본 자동저장주기 0
    const remainingTime = ref(0); // 남은 시간 (초 단위)
    const rewardNmCodes = ref([]); //신고포상제 유형 코드정보
    const vulAttackTypeCodes = ref([]); //신고포상제 유형 코드정보
    const vulEffectCodes = ref([]); //신고포상제 유형 코드정보
    const makrSeCdCodes = ref([]); // 소프트웨어 유형 대분류 코드정보
    const totalInfo = ref(reportHandler.getTotalInfo());
    const childRef = ref(null);

    // methods 정의
    const submitReport = async () => {
      showLoading();
      reportHandler
        .submitReport(lodash.cloneDeep(totalInfo.value), memberType.value)
        .then((res) => {
          hideLoading();
          if (res) {
            deleteAllIndexedDBs();
            router.go(0);
          }
        });
    };

    const changeTab = async (nextIndex) => {
      selectedTab.value = nextIndex;
      await nextTick();
      window.scrollTo({ top: 200, left: 0, behavior: "smooth" });
    };
    // 임시저장
    const tempSave = () => {
      reportHandler.tempSave(
        reportHandler.getMergedReportInfo(totalInfo.value)
      );
      startCountdown(defaultAutoSaveInterval.value);
    };
    // 임시저장 데이터 불러오기
    const loadTempData = async () => {
      const result = await reportHandler.loadTempData();

      if (Array.isArray(result) && result.length > 0) {
        const reportKeys = Object.keys(reportHandler.createDefaultReportInfo());
        const agreeKeys = Object.keys(reportHandler.createDefaultAgreeInfo());
        const personalKeys = Object.keys(
          reportHandler.createDefaultPersonalInfo()
        );
        for (const key in result[0].reportInfo) {
          let value = result[0].reportInfo[key];

          // 문자열 "true"/"false"를 boolean으로 변환
          if (value === "true") value = true;
          else if (value === "false") value = false;
          if (agreeKeys.includes(key)) {
            totalInfo.value.agree[key] = value;
          } else if (personalKeys.includes(key)) {
            totalInfo.value.personal[key] = value;
          } else {
            totalInfo.value.reportInfo[key] = value;
          }
        }
        if (result[0]?.reportCvssEval?.evlItem?.evlScore?.length > 0) {
          totalInfo.value.cvssEval = result[0].reportCvssEval;
        }
      }

      componentKey.value++;
    };

    const startCountdown = (minutes) => {
      clearInterval(countdownTimer);
      if (!minutes) {
        remainingTime.value = 0;
        return;
      }

      const intervalMs = Number(minutes) * 60;
      remainingTime.value = intervalMs;

      countdownTimer = setInterval(() => {
        if (remainingTime.value > 0) {
          remainingTime.value--;
        } else {
          reportHandler.autoSave(
            defaultAutoSaveInterval.value,
            totalInfo.value
          );
          remainingTime.value = intervalMs;
        }
      }, 1000);
    };

    // 신고포상제 세팅
    const getRewardNm = async () => {
      const result = await reportHandler.getRewardNm();
      if (Array.isArray(result)) {
        rewardNmCodes.value = result;
      }
    };
    // 공격 유형 코드 세팅
    const getVulAttackType = async () => {
      const result = await reportHandler.getVulAttackType();
      if (Array.isArray(result)) {
        vulAttackTypeCodes.value = result;
      }
    };
    // 영향 코드 세팅
    const getVulEffect = async () => {
      const result = await reportHandler.getVulEffect();
      if (Array.isArray(result)) {
        vulEffectCodes.value = result;
      }
    };

    // 소프트웨어 유형 대분류 세팅
    const getSeCode = async () => {
      const result = await reportHandler.getMakrSeCd();
      if (Array.isArray(result)) {
        makrSeCdCodes.value = result;
      }
    };

    const initCodeInfo = async () => {
      getRewardNm();
      getSeCode();
      getVulAttackType();
      getVulEffect();
    };

    const initRole = () => {
      const role = gt.getRole();
      const guestType = gt.getGuestType();

      if (!route.meta.role.includes(role)) {
        axios.setErrorSession(403);
      }

      memberType.value = role === "REPORT" ? "member" : "nonMember";

      if (
        memberType.value === "nonMember" &&
        route.meta.guestType !== guestType
      ) {
        axios.setErrorSession(403);
      }
    };

    // watch, computed 정의
    watch(defaultAutoSaveInterval, (val) => {
      startCountdown(val);
    });

    // 현재 선택된 탭 컴포넌트 반환
    const isVulInfoTab = computed(() => selectedTab.value === 1);
    const tabComponents = computed(() =>
      memberType.value === "member"
        ? [CheckList, VulInfo, VulEval]
        : [PersonalInfo, CheckList, VulInfo, VulEval]
    );

    const formattedRemainingTime = computed(() => {
      const minutes = Math.floor(remainingTime.value / 60);
      const seconds = remainingTime.value % 60;
      return `${minutes > 0 ? `${minutes}분 ` : ""}${seconds}초`;
    });

    // life cycle 정의
    onMounted(async () => {
      initRole();
      initCodeInfo();

      startCountdown(defaultAutoSaveInterval.value);

      if (memberType.value === "member") {
        const wasReload = sessionStorage.getItem("wasReload");

        if (!wasReload) {
          await nextTick();
          childRef.value[1].handleLoadFromIndexedDB();
        }
      }
      sessionStorage.removeItem("wasReload");
    });

    onUnmounted(() => {
      clearInterval(countdownTimer);
    });

    // html에서 사용될 데이터 정의
    return {
      selectedTab,
      isVulInfoTab,
      changeTab,
      memberType,
      defaultAutoSaveInterval,
      tempSave,
      loadTempData,
      remainingTime,
      formattedRemainingTime,
      rewardNmCodes,
      vulAttackTypeCodes,
      vulEffectCodes,
      makrSeCdCodes,
      totalInfo,
      submitReport,
      componentKey,
      childRef,
      tabComponents,
    };
  },
};
</script>

<style>
</style>